import { useQuery } from '@tanstack/react-query';
import {
  loadViewDataQueryFn,
  type ViewDataParams,
} from 'shared/components/CustomTable/hooks/loadViewData';
import {
  type ApiError,
  addResourceNameToQueryResult,
  useLegacyAxiosInstance,
} from 'shared/hooks/utils';

export type GetDebtorsViewParams = ViewDataParams & { view_id: string };

export type DebtorsView = Awaited<ReturnType<typeof loadViewDataQueryFn>>;

// Per-resource wrapper over the generic views endpoint (loadViewDataQueryFn).
// Replaces the redux-saga `getView` read for the debtors list. Forward-compatible:
// when the backend exposes a dedicated `GET /debtors` list endpoint, only the
// queryFn here changes — the consumer keeps the same hook contract.
const emptyDebtors: DebtorsView = { data: [], metadata: undefined };

export function useGetDebtorsView(params: GetDebtorsViewParams) {
  const axiosInstance = useLegacyAxiosInstance();
  const { view_id, ...filters } = params;

  const queryResult = useQuery<DebtorsView, ApiError>({
    queryKey: ['debtors-view', params],
    queryFn: () => loadViewDataQueryFn(axiosInstance, view_id, filters),
    enabled: Boolean(view_id),
    placeholderData: previousData => previousData ?? emptyDebtors,
  });

  return addResourceNameToQueryResult<DebtorsView, ApiError, 'debtors', 'PreventUndefined'>(
    'debtors',
    queryResult,
    emptyDebtors,
  );
}
